/**
 * Typography tokens — Brand core V13 (chap. 4).
 *
 * Font families = Outfit 300 → 900, loaded from `@expo-google-fonts/outfit` (JS + ttf, no
 * native module) — see `../fonts.ts`. Only the DS migrates: legacy screens keep reading
 * Montserrat through `src/common/theme/theme.js`.
 *
 * The family strings are the keys `useFonts()` registers, so the same value resolves on
 * native (expo-font) and on web (the `@font-face` set in `.storybook/fonts.css`).
 * The book maps Thin 100 → 300, so `thin` resolves to Light.
 *
 * Size scale = the book's steps (11 · 14 · 16 · 22 · 28 · 40) plus the in-between values the
 * app already leans on, keeping the ten existing `text-*` keys so no call-site has to move.
 *
 * Font size keys omit the `$` prefix — Unistyles CSS variables on web reject `$` in names.
 */

export const fontFamilies = {
    /** 900 — réservé au M du macaron. */
    black: "Outfit_900Black",
    extrabold: "Outfit_800ExtraBold",
    bold: "Outfit_700Bold",
    semibold: "Outfit_600SemiBold",
    medium: "Outfit_500Medium",
    regular: "Outfit_400Regular",
    light: "Outfit_300Light",
    /** Le livre remonte Thin 100 à 300 : Outfit ne descend pas plus bas. */
    thin: "Outfit_300Light",
} as const

/**
 * L'échelle du livre (23/09). L'app ne la lit plus ; elle est exportée pour ce qui la garde : le mail des
 * investisseurs (`packages/brand` → `typo.taillesLivre`).
 */
export const bookSizes = {
    "text-xxs": 9,
    /** Label — capitales, 700. */
    "text-xs": 11,
    "text-sm": 12,
    /** Petit. */
    "text-md": 14,
    /** Libellé de bouton. */
    "text-base": 15,
    /** Corps. */
    "text-lg": 16,
    "text-xl": 18,
    "text-2xl": 22,
    /** Sous-titre. */
    "text-3xl": 28,
    /** Titre. */
    "text-4xl": 40,
} as const

/**
 * L'échelle de l'app : le livre, +1 px sur les quatre crans du texte courant et des boutons, parce que le texte se
 * lisait petit (Wajih, 03/11, `design-system/DECISIONS.md` « Le texte se lit petit »). Les capitales (`text-xs`),
 * `text-xxs` et les titres gardent le livre.
 */
export const fontSizes = {
    ...bookSizes,
    "text-sm": 13,
    "text-md": 15,
    "text-base": 16,
    "text-lg": 17,
} as const

export const typography = {
    fontFamilies,
    fontSizes,
} as const

export type FontFamilyKey = keyof typeof fontFamilies
export type FontSizeKey = keyof typeof fontSizes
